/**
 * features/finance/Debtors.tsx
 * ------------------------------------------------------------------
 * THE DEBTORS MASTER — Pastel's Customers screen and customer listing.
 *
 * One row per account that owes the school money, with the four things a bursar
 * needs before picking up the phone: how much, how old, who to ring, and what
 * they last paid. Everything else is a click away.
 *
 * The account is the FAMILY, not the pupil — fees are billed per child and paid
 * per household — with the children shown underneath. A pupil with no family on
 * record still appears, marked as unlinked, because a debtor nobody can see is a
 * debt nobody collects.
 *
 * The attendance check sits inline against each pupil. It reads the daily
 * register, which finance staff can now do — firestore.rules widened
 * reportDailyAttendance to isFinanceStaff() for READ only, rather than adding
 * finance roles to isStaff() and handing them the whole academic domain.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { Search, Download, ChevronRight, ChevronDown, Phone, Mail, Users } from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useData } from '@/contexts/DataContext';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { fmtPula } from '@/lib/financeCore';
import {
  buildDebtors, debtorTotals, debtorsCsv, DEBTOR_STATUS_LABEL,
  type DebtorAccount, type DebtorStatus,
} from '@/lib/debtors';
import type { TabId } from '@/components/Shell';
import AttendanceCheckPanel from '@/features/finance/AttendanceCheck';
import { PageHeader, SoftCard, StatChip, glassFieldClass, ExportBar } from '@/features/finance/ui';
import { downloadTablePdf, downloadCsv, type TableExport } from '@/lib/tableExport';

const STATUS_TONE: Record<DebtorStatus, string> = {
  overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  owing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  settled: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  'in-credit': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  'not-billed': 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-300',
};

type Filter = 'all' | DebtorStatus;

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'owing', label: 'Owing' },
  { key: 'settled', label: 'Settled' },
  { key: 'in-credit', label: 'In credit' },
  { key: 'not-billed', label: 'Never billed' },
];

const dmy = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—'
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
};

/* onNavigate is accepted and unused for now: the per-pupil Ledger link that
   needed it returns with the Detailed Ledger in block 3, and App already
   passes it to every finance screen. Keeping it in the signature means that
   wiring is a one-line change rather than a prop threaded back through. */
export default function Debtors({ onNavigate: _onNavigate }: { onNavigate?: (t: TabId, opts?: { studentId?: string }) => void }) {
  const { students, families, classById } = useData();
  const fin = useFinanceData();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [open, setOpen] = useState<string>('');

  const rows = useMemo(() => buildDebtors({
    invoices: fin.invoices,
    payments: fin.payments,
    students: students.map((s) => ({
      id: s.id, fullName: s.fullName, familyId: s.familyId, currentClass: s.currentClass,
      status: (s as { status?: string }).status,
      leftDate: (s as { leftDate?: string }).leftDate,
    })),
    families: (families ?? []).map((f) => ({
      id: f.id,
      name: (f as { name?: string }).name,
      studentIds: (f as { studentIds?: string[] }).studentIds,
      primaryContact: (f as { primaryContact?: { name?: string; email?: string; whatsapp?: string } }).primaryContact,
    })),
    classNameOf: (id) => classById?.(id)?.name,
    thresholds: fin.settings?.agingThresholds,
  }), [fin.invoices, fin.payments, fin.settings, students, families, classById]);

  const totals = useMemo(() => debtorTotals(rows), [rows]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows
      .filter((r) => filter === 'all' || r.status === filter)
      .filter((r) => {
        if (!needle) return true;
        return [r.name, r.contactName, r.phone, r.email, ...r.pupils.map((p) => p.name), ...r.classes]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(needle));
      });
  }, [rows, filter, q]);

  function download() {
    const blob = new Blob([debtorsCsv(shown)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debtors-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const exportDoc = useMemo((): TableExport<(typeof shown)[number]> => ({
    title: 'Debtors',
    meta: `${shown.length} account${shown.length === 1 ? '' : 's'}`,
    filename: 'debtors',
    orientation: 'l',
    columns: [
      { header: 'Account', text: (r) => r.name },
      { header: 'Contact', width: 38, text: (r) => r.contactName ?? '' },
      { header: 'Phone', width: 26, text: (r) => r.phone ?? '' },
      { header: 'Pupils', text: (r) => r.pupils.map((p) => p.name).join(', ') },
      { header: 'Classes', width: 28, text: (r) => r.classes.join(', ') },
      { header: 'Billed (P)', align: 'right', width: 26, text: (r) => fmtPula(r.billed), raw: (r) => r.billed },
      { header: 'Paid (P)', align: 'right', width: 26, text: (r) => fmtPula(r.paid), raw: (r) => r.paid },
      { header: 'Balance (P)', align: 'right', width: 26, text: (r) => fmtPula(r.balance), raw: (r) => r.balance },
    ],
    rows: shown,
    totals: ['TOTAL', '', '', '', '',
      fmtPula(shown.reduce((n, r) => n + r.billed, 0)),
      fmtPula(shown.reduce((n, r) => n + r.paid, 0)),
      fmtPula(shown.reduce((n, r) => n + r.balance, 0))],
    note: 'By FAMILY, which is the account the school bills and receipts. Pupil balances net '
      + 'within a family, so a sibling in credit reduces what that family owes.',
  }), [shown]);

  if (fin.loading) return <Spinner label="Building the debtors listing..." />;

  return (
    <div className="space-y-6">
      <PageHeader icon={Users}
        title="Debtors"
        subtitle="Every account that owes the school money — how much, how old, and who to ring"
        actions={<ExportBar onPdf={() => downloadTablePdf(exportDoc)} onCsv={() => downloadCsv(exportDoc)} disabled={!shown.length} />}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatChip label="Accounts" value={String(totals.accounts)} tone="slate" />
        <StatChip label="Outstanding" value={fmtPula(totals.outstanding)} tone={totals.outstanding > 0 ? 'red' : 'emerald'} />
        <StatChip label="Collected" value={`${totals.collectionRate}%`} tone="blue" />
        <StatChip
          label={totals.overdueAccounts === 1 ? 'Account overdue' : 'Accounts overdue'}
          value={String(totals.overdueAccounts)}
          tone={totals.overdueAccounts > 0 ? 'gold' : 'emerald'}
        />
      </div>

      {/* Never-billed is called out separately: nil outstanding for the opposite
          reason to settled, and the one that is usually a billing error. */}
      {totals.notBilled > 0 && (
        <SoftCard className="p-4 text-sm">
          <span className="font-semibold">{totals.notBilled} account{totals.notBilled === 1 ? ' has' : 's have'} never been invoiced.</span>
          {' '}They show nothing outstanding for the opposite reason to a settled account — nothing
          was ever asked of them. Worth checking against the roll before the next billing run.
        </SoftCard>
      )}

      <SoftCard className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Family, pupil, class, phone or email"
              className={glassFieldClass('pl-9')}
            />
          </div>
          <button
            onClick={download}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold border-2 border-primary text-primary dark:text-primarySoft dark:border-primarySoft"
          >
            <Download size={15} /> Export listing
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => {
            const n = f.key === 'all' ? rows.length : rows.filter((r) => r.status === f.key).length;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold border-2 ${filter === f.key ? 'seg-on' : 'seg-off'}`}
              >
                {f.label} <span className="opacity-60">{n}</span>
              </button>
            );
          })}
        </div>
      </SoftCard>

      <SoftCard className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide opacity-60">
              <tr className="border-b border-black/10 dark:border-white/10">
                <th className="text-left px-4 py-2">Account</th>
                <th className="text-left px-4 py-2">Contact</th>
                <th className="text-right px-4 py-2">Billed</th>
                <th className="text-right px-4 py-2">Paid</th>
                <th className="text-right px-4 py-2">Balance</th>
                <th className="text-right px-4 py-2">Oldest</th>
                <th className="text-left px-4 py-2">Last paid</th>
                <th className="text-left px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center opacity-60">
                  Nothing matches that.
                </td></tr>
              )}
              {shown.slice(0, 300).map((r) => (
                <Row
                  key={r.id}
                  row={r}
                  open={open === r.id}
                  onToggle={() => setOpen(open === r.id ? '' : r.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
        {shown.length > 300 && (
          <div className="px-4 py-3 text-xs opacity-60 border-t border-black/10 dark:border-white/10">
            Showing the 300 largest of {shown.length} accounts. Narrow the search, or export the
            full listing — the export carries every row, not just those on screen.
          </div>
        )}
      </SoftCard>
    </div>
  );
}

function Row({
  row: r, open, onToggle,
}: {
  row: DebtorAccount;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className="border-b border-black/5 dark:border-white/5 cursor-pointer hover:bg-black/[0.03] dark:hover:bg-white/[0.04]"
      >
        <td className="px-4 py-2">
          <span className="inline-flex items-center gap-1.5 font-medium">
            {open ? <ChevronDown size={14} className="opacity-50" /> : <ChevronRight size={14} className="opacity-50" />}
            {r.name}
          </span>
          <span className="block text-xs opacity-60 ml-5">
            {r.pupils.length} pupil{r.pupils.length === 1 ? '' : 's'}
            {r.classes.length ? ` · ${r.classes.join(', ')}` : ''}
            {r.unlinked ? ' · not linked to a family' : ''}
          </span>
        </td>
        <td className="px-4 py-2 text-xs">
          {r.contactName && <span className="block">{r.contactName}</span>}
          {r.phone && <span className="block opacity-70">{r.phone}</span>}
          {!r.contactName && !r.phone && <span className="opacity-40">—</span>}
        </td>
        <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">{fmtPula(r.billed)}</td>
        <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">{fmtPula(r.paid)}</td>
        <td className={`px-4 py-2 text-right tabular-nums whitespace-nowrap font-semibold ${
          r.balance > 0 ? 'text-red-600 dark:text-red-400' : r.balance < 0 ? 'text-blue-600 dark:text-blue-400' : 'opacity-40'
        }`}>
          {fmtPula(r.balance)}
        </td>
        <td className="px-4 py-2 text-right tabular-nums text-xs whitespace-nowrap">
          {r.oldestDays > 0 ? `${r.oldestDays}d` : '—'}
        </td>
        <td className="px-4 py-2 text-xs whitespace-nowrap">
          {r.lastPaymentAt ? (
            <>
              {dmy(r.lastPaymentAt)}
              <span className="block opacity-60">{fmtPula(r.lastPaymentAmount ?? 0)}</span>
            </>
          ) : <span className="opacity-40">never</span>}
        </td>
        <td className="px-4 py-2 whitespace-nowrap">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_TONE[r.status]}`}>
            {DEBTOR_STATUS_LABEL[r.status]}
          </span>
        </td>
      </tr>

      {open && (
        <tr className="border-b border-black/5 dark:border-white/5 bg-black/[0.02] dark:bg-white/[0.03]">
          <td colSpan={8} className="px-4 py-3">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-semibold opacity-70 mb-1">Pupils on this account</div>
                <table className="w-full text-xs">
                  <tbody>
                    {r.pupils.map((p) => (
                      <tr key={p.id} className="border-b border-black/5 dark:border-white/5 last:border-0">
                        <td className="py-1 pr-3">
                          {p.name}
                          {p.status === 'left' && (
                            <span className="ml-2 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300">
                              Left
                            </span>
                          )}
                        </td>
                        <td className="py-1 pr-3 opacity-60">{p.className ?? ''}</td>
                        <td className="py-1 text-right tabular-nums">{fmtPula(p.balance)}</td>
                        <td className="py-1 pl-3 text-right">
                          <div
                            className="inline-flex items-center gap-3"
                            onClick={(e) => e.stopPropagation()}
                            role="presentation"
                          >
                            {/* Whether the child is still in school belongs
                                next to what they owe, not in another module.
                                A family that owes P18,000 and left in June is
                                a different conversation from one at the gate
                                every morning. */}
                            <AttendanceCheckPanel studentId={p.id} classId={p.classId} />
                            {/* The per-pupil Ledger link belongs here too and
                                returns with the Detailed Ledger (block 3). */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <div className="text-xs font-semibold opacity-70 mb-1">How old the debt is</div>
                <table className="w-full text-xs">
                  <tbody>
                    <tr className="border-b border-black/5 dark:border-white/5">
                      <td className="py-1">Not yet due</td>
                      <td className="py-1 text-right tabular-nums">{fmtPula(r.aging.current)}</td>
                    </tr>
                    {r.aging.buckets.map((b) => (
                      <tr key={b.label} className="border-b border-black/5 dark:border-white/5 last:border-0">
                        <td className="py-1">{b.label}</td>
                        <td className="py-1 text-right tabular-nums">{fmtPula(b.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(r.phone || r.email) && (
                  <div className="flex flex-wrap gap-3 mt-2 text-xs">
                    {r.phone && (
                      <a href={`tel:${r.phone}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 font-semibold text-primary dark:text-primarySoft hover:underline">
                        <Phone size={12} /> {r.phone}
                      </a>
                    )}
                    {r.email && (
                      <a href={`mailto:${r.email}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 font-semibold text-primary dark:text-primarySoft hover:underline">
                        <Mail size={12} /> {r.email}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
